import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Form, Modal, Button } from "react-bootstrap";
import SunEditor from "suneditor-react";
import DatePicker from "./AppDatePicker";
import moment from "moment-timezone";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import TimePicker from "./TimePicker";
import RetreatTeamSection from "./RetreatTeamSection";
import { formatParishOptionLabel } from "../utils/parishLabels";
import ViewMailMsg from "../components/ViewMailMsg";
import AlertDelete from "../components/AlertDelete";
import DescriptionSampleTip from "./DescriptionSampleTip";
import SampleContentApi from "../api/SampleContentApi";
import {
  getRetreatImagePreviewSrc,
  RETREAT_IMAGE_ACCEPT,
} from "../utils/processRetreatImage";
import RetreatImageUploadInfo from "./RetreatImageUploadInfo";
import { renderRetreatDateHeader } from "./retreatDateHeader";
import "../assets/css/RetreatFormModal.css";

const SUN_EDITOR_OPTIONS = {
  buttonList: [
    ["undo", "redo", "font", "fontSize", "formatBlock"],
    ["bold", "underline", "italic", "removeFormat"],
    [
      "fontColor",
      "hiliteColor",
      "outdent",
      "indent",
      "align",
      "horizontalRule",
      "list",
    ],
    ["fullScreen", "link", "save"],
  ],
};

const FORM_SECTIONS = [
  {
    id: "rfm-basics",
    label: "Basics",
    detail: "Parish and location",
    icon: "mdi-map-marker-radius",
  },
  {
    id: "rfm-schedule",
    label: "Schedule",
    detail: "Dates and times",
    icon: "mdi-calendar-range",
  },
  {
    id: "rfm-story",
    label: "Messages",
    detail: "Description and Email",
    icon: "mdi-text-box-outline",
  },
  {
    id: "rfm-team",
    label: "Team",
    detail: "Retreat roles",
    icon: "mdi-account-group",
  },
  {
    id: "rfm-access",
    label: "Access",
    detail: "Administrators",
    icon: "mdi-shield-account",
  },
];

const ADMIN_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    borderRadius: 12,
    borderColor: state.isFocused ? "#8b1e3f" : "#eadfe3",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(139, 30, 63, 0.14)" : "none",
    "&:hover": { borderColor: "#8b1e3f" },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  multiValue: (base) => ({
    ...base,
    borderRadius: 999,
    background: "#fff5f7",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#6f1732",
    fontWeight: 700,
  }),
  placeholder: (base) => ({ ...base, color: "#8d7b82" }),
};

const hasRichText = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim().length > 0;

const SCHEDULE_DAYS = [
  {
    index: 0,
    labelKey: "friday",
    dayFilter: 5,
    dateKey: "date0",
    fromKey: "from0",
    toKey: "to0",
  },
  {
    index: 1,
    labelKey: "saturday",
    dayFilter: 6,
    dateKey: "date1",
    fromKey: "from1",
    toKey: "to1",
  },
  {
    index: 2,
    labelKey: "sunday",
    dayFilter: 0,
    dateKey: "date2",
    fromKey: "from2",
    toKey: "to2",
  },
];

function RetreatFormModal({
  show,
  onHide,
  isEdit,
  validated,
  data,
  perishDataList,
  description,
  setDescription,
  emailMsg,
  setEmailMsg,
  roles,
  rolesDataList,
  allCoupleData,
  priestsList,
  retreatId,
  getRolesOfRetreats,
  onTeamChange,
  onPeopleRefresh,
  setEditIndex,
  editIndex,
  allCoupleMails,
  coupleSelectForAccess,
  accessGreenTick,
  check,
  timePickerPass,
  formattedDate,
  upload,
  corruptedImg,
  uploadRetreat,
  handleChanges,
  handleTimeChange,
  handleSubmit,
  handleSelectOption,
  handleAccessSubmit,
  handlerAccessRmvShow,
  showIsDisabledPopUp,
  remove,
  dynamicDescription,
  dynamicEmailMsg,
}) {
  const { t } = useTranslation();
  const [descriptionSample, setDescriptionSample] = useState(false);
  const [mailSample, setMailSample] = useState(false);
  const [mailMsgView, setMailMsgView] = useState(false);
  const [sampleDescription, setSampleDescription] = useState(
    dynamicDescription || "",
  );
  const [sampleEmail, setSampleEmail] = useState(dynamicEmailMsg || "");
  const sampleContentApi = useMemo(() => new SampleContentApi(), []);

  useEffect(() => {
    setSampleDescription(dynamicDescription || "");
  }, [dynamicDescription]);

  useEffect(() => {
    setSampleEmail(dynamicEmailMsg || "");
  }, [dynamicEmailMsg]);

  const loadDescriptionAndEmailSamples = () => {
    const selectedLang = data?.language || "english";
    sampleContentApi
      .getSampleContent("Retreat Description", selectedLang)
      .then((res) => {
        if (res.success && res.data) setSampleDescription(res.data.content);
      });
    sampleContentApi
      .getSampleContent("Retreat Email Message", selectedLang)
      .then((res) => {
        if (res.success && res.data) setSampleEmail(res.data.content);
      });
  };
  const imagePreviewSrc = useMemo(
    () => getRetreatImagePreviewSrc(data?.image1),
    [data?.image1],
  );

  const handleModalSubmit = (e) => {
    handleSubmit(e, { onSuccess: onHide });
  };

  const scrollRef = useRef(null);
  const [activeSection, setActiveSection] = useState(FORM_SECTIONS[0].id);

  const completion = useMemo(() => {
    const items = [
      Boolean(data?.parish),
      Boolean(data?.language),
      Boolean(String(data?.location || "").trim()),
      Boolean(String(data?.paymentLink || "").trim()),
      ...SCHEDULE_DAYS.map((day) => {
        const dateValue = data?.schedule?.[day.index]?.date;
        return Boolean(dateValue && dateValue !== "Invalid date");
      }),
    ];
    const done = items.filter(Boolean).length;
    return {
      done,
      total: items.length,
      percent: Math.round((done / items.length) * 100),
    };
  }, [data]);

  const sectionReady = useMemo(
    () => ({
      "rfm-basics":
        Boolean(data?.parish) &&
        Boolean(data?.language) &&
        Boolean(String(data?.location || "").trim()) &&
        Boolean(String(data?.paymentLink || "").trim()),
      "rfm-schedule": SCHEDULE_DAYS.every((day) => {
        const dateValue = data?.schedule?.[day.index]?.date;
        return Boolean(dateValue && dateValue !== "Invalid date");
      }),
      "rfm-story": hasRichText(description) || hasRichText(emailMsg),
      "rfm-team": Boolean(
        roles?.leadCouple ||
          roles?.coLeadCouple ||
          roles?.mentor ||
          roles?.spiritualDirector ||
          roles?.spiritualMentor,
      ),
      "rfm-access": Boolean(data?.userEmails?.length),
    }),
    [data, description, emailMsg, roles],
  );

  const sectionObserver = useRef(null);

  useEffect(() => {
    if (show) setActiveSection(FORM_SECTIONS[0].id);
  }, [show]);

  const bindScroll = useCallback((node) => {
    if (sectionObserver.current) {
      sectionObserver.current.disconnect();
      sectionObserver.current = null;
    }
    scrollRef.current = node;
    if (!node || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) setActiveSection(visible.target.id);
      },
      {
        root: node,
        rootMargin: "-12% 0px -55% 0px",
        threshold: [0.15, 0.4, 0.75],
      },
    );
    FORM_SECTIONS.forEach((section) => {
      const target = node.querySelector(`#${section.id}`);
      if (target) observer.observe(target);
    });
    sectionObserver.current = observer;
  }, []);

  const scrollToSection = (id) => {
    setActiveSection(id);
    const root = scrollRef.current;
    const target = document.getElementById(id);
    if (!root || !target) return;
    const top =
      target.getBoundingClientRect().top -
      root.getBoundingClientRect().top +
      root.scrollTop -
      12;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    root.scrollTo({ top, behavior: reduceMotion ? "auto" : "smooth" });
  };

  const scheduleDate = (index) => {
    const dateValue = data?.schedule?.[index]?.date;
    if (!dateValue || dateValue === "Invalid date") return null;
    const parsed = moment(formattedDate(dateValue));
    return parsed.isValid() ? parsed.toDate() : null;
  };

  const handleCloseAlert = () => {
    setDescriptionSample(false);
    setMailSample(false);
  };

  return (
    <div className="tab_headers">
      {typeof document !== "undefined"
        ? createPortal(<div id="rfm-datepicker-portal" />, document.body)
        : null}
      <Modal
        show={show}
        onHide={onHide}
        backdrop="static"
        keyboard={false}
        enforceFocus={false}
        mountOnEnter
        unmountOnExit
        dialogClassName="rfm-dialog"
        contentClassName="rfm-content"
        className="rfm"
      >
        <div className="rfm-shell">
          <header className="rfm-header">
            <div className="rfm-header-copy">
              <p className="rfm-kicker">
                {isEdit ? "Editing retreat" : "New retreat"}
              </p>
              <h2>{isEdit ? t("edit_retreat") : t("register_new_retreat")}</h2>
              <p>Complete the full retreat details, then save.</p>
            </div>
            <div className="rfm-progress">
              <div
                className="rfm-ring"
                style={{ "--rfm-pct": `${completion.percent}%` }}
                aria-hidden="true"
              >
                <span>{completion.percent}%</span>
              </div>
              <div>
                <strong>
                  {completion.done}/{completion.total} ready
                </strong>
                <small>Required details</small>
              </div>
            </div>
            <button
              type="button"
              className="rfm-close"
              onClick={onHide}
              aria-label="Close"
            >
              <i className="mdi mdi-close" />
            </button>
          </header>

          <div className="rfm-layout">
            <nav className="rfm-nav" aria-label="Retreat form sections">
              {FORM_SECTIONS.map((section, index) => (
                <button
                  key={section.id}
                  type="button"
                  className={`${activeSection === section.id ? "is-active" : ""} ${
                    sectionReady[section.id] ? "is-ready" : ""
                  }`}
                  aria-current={activeSection === section.id ? "true" : undefined}
                  onClick={() => scrollToSection(section.id)}
                >
                  <i className={`mdi ${section.icon}`} />
                  <span>
                    <span>
                      {index + 1}. {section.label}
                    </span>
                    <small>{section.detail}</small>
                  </span>
                </button>
              ))}
            </nav>

            <div className="rfm-scroll" ref={bindScroll}>
              <Form
                id="retreat-modal-form"
                onSubmit={handleModalSubmit}
                noValidate
                validated={validated}
                className="forms-sample"
              >
                <section className="rfm-section" id="rfm-basics">
                  <div className="rfm-section-head">
                    <div>
                      <h3>
                        <span className="rfm-step">1</span>
                        Retreat basics
                      </h3>
                      <p>Parish, language, location, and the image couples see.</p>
                    </div>
                  </div>
                  <div className="rfm-basics-grid">
                    <div className="rfm-field-grid">
                      <Form.Group className="form-group rfm-field">
                        <Form.Label>
                          {t("enter_Parish_name")}{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          required
                          name="parish"
                          value={data?.parish || ""}
                          onChange={(e) => handleChanges("", e)}
                        >
                          <option value="">{t("select_parish")}</option>
                          {perishDataList
                            ?.filter(
                              (item) =>
                                item.parishCategoryName?.toUpperCase() !==
                                "OTHER",
                            )
                            .map((item) => (
                              <option
                                key={`modal-parish-${item.id}`}
                                value={item.id}
                              >
                                {formatParishOptionLabel(item)}
                              </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {t("parish_required")}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="form-group rfm-field">
                        <Form.Label>
                          {t("select_retreat_language")}{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          required
                          name="language"
                          value={data?.language || ""}
                          onChange={(e) => handleChanges("", e)}
                        >
                          <option value="">{t("select")}</option>
                          <option value="english">English</option>
                          <option value="spanish">Spanish</option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {t("language_required")}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="form-group rfm-field">
                        <Form.Label>
                          {t("enter_location")}{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          required
                          type="text"
                          name="location"
                          placeholder="Location"
                          value={data?.location || ""}
                          onChange={(e) => handleChanges("", e)}
                        />
                        <Form.Control.Feedback type="invalid">
                          {t("location_required")}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="form-group rfm-field">
                        <Form.Label>{t("retreat_theme")}</Form.Label>
                        <Form.Control
                          type="text"
                          name="heading"
                          placeholder={t("retreat_theme")}
                          value={data?.heading || ""}
                          onChange={(e) => handleChanges("", e)}
                        />
                        <Form.Control.Feedback type="invalid">
                          {t("retreat_theme_required")}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="form-group rfm-field rfm-span-2">
                        <Form.Label>
                          {t("retreat_payment_link")}{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          required
                          type="text"
                          name="paymentLink"
                          placeholder="Retreat Payment Link"
                          value={data?.paymentLink || ""}
                          onChange={(e) => handleChanges("", e)}
                        />
                        <Form.Control.Feedback type="invalid">
                          Payment link is required!
                        </Form.Control.Feedback>
                      </Form.Group>
                    </div>

                    <div className="rfm-media">
                      <div
                        className={`rfm-media-frame${
                          data?.image1 ? " has-image" : ""
                        }`}
                      >
                        <img
                          src={data?.image1 ? imagePreviewSrc : uploadRetreat}
                          alt={data?.image1 ? "Retreat" : ""}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = corruptedImg || uploadRetreat;
                          }}
                        />
                      </div>
                      <input
                        name="image1"
                        type="file"
                        accept={RETREAT_IMAGE_ACCEPT}
                        style={{ display: "none" }}
                        id="retreatImageUpload1_modal"
                        onChange={(e) => handleChanges("", e)}
                      />
                      <div className="rfm-media-actions">
                        {!data?.image1 ? (
                          <>
                            <Button
                              variant="primary"
                              className="rfm-upload-btn"
                              type="button"
                              onClick={() => upload("ForFirstModal")}
                            >
                              {t("upload_retreat_image")}
                            </Button>
                            <RetreatImageUploadInfo />
                          </>
                        ) : (
                          <Button
                            variant="danger"
                            className="rfm-remove-btn"
                            type="button"
                            onClick={() => remove("ForFirst")}
                          >
                            {t("remove")}
                          </Button>
                        )}
                      </div>
                      <p className="rfm-media-note">
                        This image appears on the retreat page.
                      </p>
                    </div>
                  </div>
                </section>

                <section className="rfm-section" id="rfm-schedule">
                  <div className="rfm-section-head">
                    <div>
                      <h3>
                        <span className="rfm-step">2</span>
                        Weekend schedule
                      </h3>
                      <p>Pick the Friday, Saturday, and Sunday for this retreat.</p>
                    </div>
                  </div>
                  <div className="rfm-days">
                    {SCHEDULE_DAYS.map((day) => (
                      <div className="rfm-day" key={day.index}>
                        <h4>{t(day.labelKey)}</h4>
                        <Form.Group className="form-group rfm-field">
                          <Form.Label>
                            {t("date")}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <DatePicker
                            autoComplete="off"
                            dateFormat="MM-dd-yyyy"
                            placeholderText="mm-dd-yyyy"
                            className="form-control"
                            selected={scheduleDate(day.index)}
                            onChange={(date) =>
                              handleTimeChange(day.dateKey, date, day.index)
                            }
                            filterDate={(date) => date.getDay() === day.dayFilter}
                            renderCustomHeader={renderRetreatDateHeader}
                            showPopperArrow={false}
                            popperClassName="rfm-date-popper"
                            popperProps={{ strategy: "fixed" }}
                            portalId="rfm-datepicker-portal"
                          />
                          <Form.Control.Feedback type="invalid">
                            {t("date_required")}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <div className="rfm-times">
                          <Form.Group className="form-group rfm-field">
                            <Form.Label>{t("from")}</Form.Label>
                            <TimePicker
                              chekPicker={timePickerPass}
                              useTwelveHourFormat={true}
                              showClockIcon={false}
                              showCloseIcon={true}
                              allowBackdrop={true}
                              onTimeChange={(time) =>
                                handleTimeChange(day.fromKey, time, day.index)
                              }
                              placeholder="00 : 00 am"
                              defaulValue="00 : 00"
                              value={data?.schedule?.[day.index]?.from}
                              className="timePicker"
                            />
                            <Form.Control.Feedback type="invalid">
                              {t("time_from_required")}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <Form.Group className="form-group rfm-field">
                            <Form.Label>{t("to")}</Form.Label>
                            <TimePicker
                              chekPicker={timePickerPass}
                              useTwelveHourFormat={true}
                              showClockIcon={false}
                              showCloseIcon={true}
                              allowBackdrop={true}
                              onTimeChange={(time) =>
                                handleTimeChange(day.toKey, time, day.index)
                              }
                              placeholder="00 : 00 am"
                              defaulValue="00 : 00"
                              value={data?.schedule?.[day.index]?.to}
                              className="timePicker indexVlues"
                            />
                            <Form.Control.Feedback type="invalid">
                              {t("time_to_required")}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </div>
                      </div>
                    ))}
                  </div>
                  {check === "0" || check === "1" || check === "2" ? (
                    <p className="rfm-time-error">{t("time_error")}</p>
                  ) : null}
                </section>

                <section className="rfm-section" id="rfm-story">
                  <div className="rfm-section-head">
                    <div>
                      <h3>
                        <span className="rfm-step">3</span>
                        Messages
                      </h3>
                      <p>What couples read on the page, and the email they receive.</p>
                    </div>
                  </div>

                  <div className="rfm-editor-card">
                    <Form.Group
                      className="form-group rfm-field"
                      controlId="modal-description-editor"
                    >
                      <div className="rfm-section-head">
                        <Form.Label className="mb-0">{t("description")}</Form.Label>
                        <div className="rfm-editor-tools">
                          <button
                            type="button"
                            className="rfm-link-btn"
                            onClick={() => {
                              loadDescriptionAndEmailSamples();
                              setDescriptionSample(true);
                            }}
                          >
                            {t("description_sample")}
                          </button>
                        </div>
                      </div>
                      <SunEditor
                        height="220px"
                        setContents={description}
                        onChange={(content) => setDescription(content)}
                        name="description"
                        setOptions={SUN_EDITOR_OPTIONS}
                      />
                      <Form.Control.Feedback type="invalid">
                        Description is required!
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>

                  <div className="rfm-editor-card">
                    <Form.Group
                      className="form-group rfm-field"
                      controlId="modal-email-editor"
                    >
                      <div className="rfm-section-head">
                        <Form.Label className="mb-0">{t("email_message")}</Form.Label>
                        <div className="rfm-editor-tools">
                          <button
                            type="button"
                            className="rfm-link-btn"
                            onClick={() => setMailMsgView(true)}
                          >
                            {t("email_preview")}
                          </button>
                          <button
                            type="button"
                            className="rfm-link-btn"
                            onClick={() => {
                              loadDescriptionAndEmailSamples();
                              setMailSample(true);
                            }}
                          >
                            {t("sample_email_message")}
                          </button>
                        </div>
                      </div>
                      <SunEditor
                        height="220px"
                        setContents={emailMsg}
                        onChange={(content) => setEmailMsg(content)}
                        name="mail_msg"
                        setOptions={SUN_EDITOR_OPTIONS}
                      />
                      <Form.Control.Feedback type="invalid">
                        Email message is required!
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                </section>

                <section className="rfm-section" id="rfm-team">
                  <div className="rfm-section-head">
                    <div>
                      <h3>
                        <span className="rfm-step">4</span>
                        {t("retreat_team")}
                      </h3>
                      <p>Assign the couples and priest who lead this weekend.</p>
                    </div>
                  </div>
                  {!retreatId ? (
                    <p className="rfm-hint">
                      Save the retreat first, then assign team roles.
                    </p>
                  ) : null}
                  <RetreatTeamSection
                    allCoupleData={allCoupleData}
                    priestsList={priestsList}
                    rolesDataList={rolesDataList}
                    roles={roles}
                    retreatId={retreatId}
                    getRolesOfRetreats={getRolesOfRetreats}
                    onTeamChange={onTeamChange}
                    onPeopleRefresh={onPeopleRefresh}
                    setEditIndex={setEditIndex}
                    editIndex={editIndex}
                  />
                </section>

                <section className="rfm-section" id="rfm-access">
                  <div className="rfm-section-head">
                    <div>
                      <h3>
                        <span className="rfm-step">5</span>
                        {t("retreat_administrators")}
                      </h3>
                      <p>Choose who can manage registrations for this retreat.</p>
                    </div>
                  </div>
                  {!retreatId ? (
                    <p className="rfm-hint">
                      Administrators can be added after the retreat is saved.
                    </p>
                  ) : null}
                  <div className="rfm-access-row">
                    <Select
                      className="rfm-select"
                      styles={ADMIN_SELECT_STYLES}
                      options={allCoupleMails}
                      value={coupleSelectForAccess}
                      onChange={handleSelectOption}
                      isSearchable={true}
                      isMulti={true}
                      menuPlacement="auto"
                      menuPosition="fixed"
                      menuPortalTarget={document.body}
                      placeholder={t("select_couple")}
                    />
                    <button
                      type="button"
                      className="rfm-add-btn"
                      onClick={handleAccessSubmit}
                      disabled={Boolean(accessGreenTick)}
                    >
                      <i
                        className={`mdi ${
                          accessGreenTick ? "mdi-check" : "mdi-plus"
                        }`}
                      />
                      {accessGreenTick ? "Added" : "Add"}
                    </button>
                  </div>

                  {data?.userEmails?.length > 0 ? (
                    <div className="rfm-admin-list">
                      {data.userEmails.map((item, index) => (
                        <div className="rfm-admin" key={`${item.label}-${index}`}>
                          <label>
                            <input
                              type="checkbox"
                              checked={item.isDisabled !== true}
                              onChange={() => showIsDisabledPopUp(data, item)}
                            />
                            <span>{item.label}</span>
                          </label>
                          <button
                            type="button"
                            className="rfm-icon-btn"
                            onClick={() => handlerAccessRmvShow(item.label)}
                            aria-label="Remove administrator"
                            title="Remove Couple"
                          >
                            <i className="mdi mdi-delete-forever" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="rfm-empty">{t("no_couple_yet")}</p>
                  )}
                </section>
              </Form>
            </div>
          </div>

          <footer className="rfm-footer">
            <p>Required fields are marked with *.</p>
            <div className="rfm-footer-actions">
              <button type="button" className="rfm-btn rfm-btn-quiet" onClick={onHide}>
                {t("cancel")}
              </button>
              <button
                type="submit"
                form="retreat-modal-form"
                className="rfm-btn rfm-btn-solid"
              >
                {isEdit ? t("update") : t("save")}
              </button>
            </div>
          </footer>
        </div>
        {descriptionSample ? (
          <AlertDelete
            show={descriptionSample}
            alertHeader={t("description_sample")}
            alerMessage={
              <div>
                <DescriptionSampleTip />
                {sampleDescription ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: sampleDescription }}
                  />
                ) : data.language === "spanish" ? (
                  <div style={{ textAlign: "center" }}>
                    <p style={{ color: "rgb(53, 53, 53)", fontSize: "16px" }}>
                      El costo es de $175 por pareja. Se requiere un depósito de
                      $75 al momento de enviar el formulario de inscripción. El
                      número de parejas que podemos acomodar es limitado y la
                      disponibilidad es por orden de llegada. El costo de
                      inscripción cubre la cena del viernes, tres comidas el
                      sábado y el desayuno y almuerzo del domingo. También cubre
                      todos los materiales del retiro. No se proporciona cuidado
                      de niños.
                    </p>

                    <p>
                      <strong>
                        <em>
                          Todas las parejas del retiro deben comprometerse a
                          asistir a todas las actividades del fin de semana de 3
                          días.
                        </em>
                      </strong>
                    </p>

                    <p style={{ fontSize: "20px", color: "rgb(34, 34, 34)" }}>
                      Iglesia de San Pedro y San Pablo
                      <br />
                      386 N Castell Ave.,
                      <br />
                      New Braunfels, TX, 78130
                    </p>

                    <p>Para preguntas, aquí está la información de contacto:</p>

                    <p>
                      <a
                        href="mailto:solizspp.lovestrong@gmail.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1155CC" }}
                      >
                        solizspp.lovestrong@gmail.com
                      </a>
                    </p>

                    <p style={{ color: "#222222" }}>
                      Phil (210-779-3096)
                      <br />
                      Jen (210-288-1908)
                    </p>

                    <p>¡Gracias!</p>
                  </div>
                ) : (
                  <div style={{ textAlign: "center" }}>
                    <p style={{ color: "rgb(53, 53, 53)", fontSize: "16px" }}>
                      The cost is $175 per couple. A $75 deposit is due with the
                      registration form. We are limited as to the number of
                      couples we can accommodate, and availability is on a first
                      come, first-serve basis. The registration cost covers
                      dinner on Friday, three meals on Saturday, and breakfast
                      and lunch on Sunday. It will also cover all retreat
                      materials. Childcare is not provided.
                    </p>

                    <p>
                      <strong>
                        <em>
                          All Retreat Couples must commit to attending the
                          entire 3-day weekend’s activities.
                        </em>
                      </strong>
                    </p>

                    <p style={{ fontSize: "20px", color: "rgb(34, 34, 34)" }}>
                      Sts. Peter and Paul Church
                      <br />
                      386 N Castell Ave.,
                      <br />
                      New Braunfels, TX, 78130
                    </p>

                    <p>For questions, here is the contact info:</p>

                    <p>
                      <a
                        href="mailto:solizspp.lovestrong@gmail.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1155CC" }}
                      >
                        solizspp.lovestrong@gmail.com
                      </a>
                    </p>

                    <p style={{ color: "#222222" }}>
                      Phil (210-779-3096)
                      <br />
                      Jen (210-288-1908)
                    </p>

                    <p>Thank you!</p>
                  </div>
                )}
              </div>
            }
            handleClose={handleCloseAlert}
            buttonText={t("close")}
          />
        ) : null}

        {mailSample ? (
          <AlertDelete
            show={mailSample}
            alertHeader={t("sample_email_message")}
            alerMessage={
              <div>
                {sampleEmail ? (
                  <div dangerouslySetInnerHTML={{ __html: sampleEmail }} />
                ) : data.language === "spanish" ? (
                  <div style={{ textAlign: "center" }}>
                    <p>
                      Gracias por inscribirse en el retiro matrimonial
                      LOVESTRONG de San Pedro y San Pablo, programado del 18 al
                      20 de octubre de 2024. ¡Nos entusiasma que nos acompañen!
                    </p>
                    <p>Si tienes alguna pregunta, contáctanos aquí...</p>
                    <p>
                      <strong>Correo electrónico: </strong>
                      <a
                        href="mailto:solizspp.lovestrong@gmail.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1155CC" }}
                      >
                        solizspp.lovestrong@gmail.com
                      </a>
                    </p>
                    <p style={{ color: "rgb(34, 34, 34)" }}>
                      <strong>Jen Soliz</strong>
                      <br />
                      210-288-1908
                    </p>
                    <p style={{ color: "#222222" }}>
                      <strong>Phil Soliz</strong>
                      <br />
                      210-779-3096
                    </p>
                    <p style={{ fontSize: "12px" }}>
                      Por favor, espere saber de nosotras pronto!
                    </p>
                    <p style={{ fontSize: "12px" }}>
                      <strong>¡VIVE! ¡AMA! ¡FUERTE!</strong>
                    </p>
                  </div>
                ) : (
                  <div style={{ textAlign: "center" }}>
                    <p>
                      Thank you for registering for the Sts. Peter and Paul
                      LOVESTRONG Marriage retreat scheduled for October 18-20,
                      2024. We are excited that you will join us!
                    </p>
                    <p>
                      If you have any questions, please reach out to us here...
                    </p>
                    <p>
                      <strong>Email: </strong>
                      <a
                        href="mailto:solizspp.lovestrong@gmail.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1155CC" }}
                      >
                        solizspp.lovestrong@gmail.com
                      </a>
                    </p>
                    <p style={{ color: "rgb(34, 34, 34)" }}>
                      <strong>Jen Soliz</strong>
                      <br />
                      210-288-1908
                    </p>
                    <p style={{ color: "#222222" }}>
                      <strong>Phil Soliz</strong>
                      <br />
                      210-779-3096
                    </p>
                    <p style={{ fontSize: "12px" }}>
                      Please expect to hear from us soon!
                    </p>
                    <p style={{ fontSize: "12px" }}>
                      <strong>LIVE! LOVE! STRONG!</strong>
                    </p>
                  </div>
                )}
              </div>
            }
            handleClose={handleCloseAlert}
            buttonText={t("close")}
          />
        ) : null}

        {mailMsgView ? (
          <ViewMailMsg
            show={mailMsgView}
            alertHeader={t("preview_email")}
            alertMessage={emailMsg}
            handleClose={() => {
              setMailMsgView(false);
            }}
            language={data.language}
          ></ViewMailMsg>
        ) : null}
      </Modal>
    </div>
  );
}

export default RetreatFormModal;
